<template>
  <div id="app" class="min-h-screen bg-gray-50">
    <!-- Nav bar — only shown when authenticated -->
    <nav v-if="authStore.isAuthenticated" class="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
      <div class="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
        <!-- Logo / brand -->
        <div class="flex items-center gap-6">
          <RouterLink to="/dashboard" class="flex items-center gap-2 text-blue-600 font-semibold text-sm hover:text-blue-700 transition-colors">
            <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 14l9-5-9-5-9 5 9 5z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 14l6.16-3.422A12.083 12.083 0 0121 12c0 3.866-3.582 7-8 7s-8-3.134-8-7a12.083 12.083 0 012.84-1.578L12 14z" />
            </svg>
            Student Portal
          </RouterLink>

          <!-- Nav links -->
          <div class="hidden sm:flex items-center gap-1">
            <RouterLink
              to="/dashboard"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="$route.path === '/dashboard' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
            >
              Dashboard
            </RouterLink>
            <RouterLink
              to="/timetable"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="$route.path === '/timetable' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
            >
              Timetable
            </RouterLink>
            <RouterLink
              to="/grades"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="$route.path === '/grades' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
            >
              Grades
            </RouterLink>
            <RouterLink
              to="/attendance"
              class="px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              :class="$route.path === '/attendance' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'"
            >
              Attendance
            </RouterLink>
          </div>
        </div>

        <!-- Student name + logout -->
        <div class="flex items-center gap-3">
          <span class="hidden sm:block text-sm text-gray-600 font-medium">
            {{ userDisplayName }}
          </span>
          <button
            @click="handleLogout"
            :disabled="authStore.isLoading"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
        </div>
      </div>

      <!-- Mobile nav links -->
      <div class="sm:hidden flex overflow-x-auto border-t border-gray-100 px-4 py-1 gap-1 bg-gray-50">
        <RouterLink
          to="/dashboard"
          class="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="$route.path === '/dashboard' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
        >Dashboard</RouterLink>
        <RouterLink
          to="/timetable"
          class="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="$route.path === '/timetable' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
        >Timetable</RouterLink>
        <RouterLink
          to="/grades"
          class="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="$route.path === '/grades' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
        >Grades</RouterLink>
        <RouterLink
          to="/attendance"
          class="flex-shrink-0 whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          :class="$route.path === '/attendance' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-100'"
        >Attendance</RouterLink>
      </div>
    </nav>

    <!-- Page content -->
    <RouterView />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink, RouterView, useRouter } from 'vue-router';
import { useAuthStore } from './stores/auth';

const authStore = useAuthStore();
const router = useRouter();

const userDisplayName = computed(() => {
  const user = authStore.user;
  if (!user) return '';
  return `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || '';
});

async function handleLogout() {
  await authStore.logout();
  router.push('/login');
}
</script>
